"use client";

import { useEffect } from "react";

import Link from "next/link";
import { redirect } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { signInSchema, type SignInSchema } from "@/app/(internal)/auth/sign-in/_schemas/sign-in.schema";
import Button from "@/components/flowbite/button/button";
import Divider from "@/components/flowbite/divider/divider";
import Input from "@/components/flowbite/forms/fields/input/input";
import PasswordInput from "@/components/flowbite/forms/fields/input/password-input";
import FormCard from "@/components/flowbite/forms/form-card";
import FormTitle from "@/components/flowbite/forms/form-title";
import FormWrapper from "@/components/flowbite/forms/form-wrapper";
import Google from "@/components/flowbite/logo/google";
import Microsoft from "@/components/flowbite/logo/microsoft";
import { dictionaries } from "@/dictionaries/content";
import { useClientErrors } from "@/providers/error-handler/hooks/client-errors.hook";
import { ENavigationKeys } from "@/providers/navigation/enums/navigation-keys.enum";
import { useNavigation } from "@/providers/navigation/hooks/navigation-matcher.hook";
import { useGoogleLinkMutation, useMicrosoftLinkMutation, useSignInMutation } from "@/providers/store/api/auth/auth";
import { type ISignInData } from "@/providers/store/api/auth/auth.interface";
import { useToast } from "@/providers/toasts/toasts.provider";
import hasErrors from "@/shared/helpers/has-errors.helper";


export default function Page() {
  const { router, navigation } = useNavigation();
  const { showToast } = useToast();

  const [signIn, { isSuccess }] = useSignInMutation();
  const [googleLink] = useGoogleLinkMutation();
  const [microsoftLink] = useMicrosoftLinkMutation();

  const {
    control, handleSubmit, formState: { errors }, clearErrors, watch, setError,
  } = useForm<SignInSchema>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { unhandledError, triggerClearErrors } = useClientErrors(
    setError,
    watch,
    clearErrors,
    errors,
    { schema: signInSchema },
  );

  useEffect(() => {
    if (unhandledError) {
      showToast({
        type: "error",
        message: unhandledError,
      });
    }
  }, [showToast, unhandledError]);

  useEffect(() => {
    if (isSuccess) {
      router.push(navigation.getRouteHref(ENavigationKeys.Dashboard) ?? "");
    }
  }, [isSuccess, navigation, router]);

  const handleGoogle = async () => {
    const { url } = await googleLink().unwrap();

    redirect(url);
  };

  const handleMicrosoft = async () => {
    const { url } = await microsoftLink().unwrap();

    redirect(url);
  };

  const onSubmit = (data: ISignInData) => {
    triggerClearErrors();
    void signIn(data).unwrap();
  };

  const email = watch("email");
  const password = watch("password");

  const forgotPasswordHref = navigation.getRouteHref(ENavigationKeys.ForgotPassword)!;

  return (
    <FormCard classNames="w-[424px] flex flex-col gap-[34px]">
      <FormTitle>{dictionaries.signIn.title}</FormTitle>
      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Input
                {...field}
                error={!!errors.email?.message}
                errorMessage={errors.email?.message}
                label={dictionaries.signIn.email.label}
                placeholder={dictionaries.signIn.email.placeholder}
                type="email"
              />
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <PasswordInput
                {...field}
                error={!!errors.password?.message}
                errorMessage={errors.password?.message}
                label={dictionaries.signIn.password.label}
                placeholder={dictionaries.signIn.password.placeholder}
              />
            )}
          />

          <Link
            className="text-light-600 text-sm font-normal leading-[20px]"
            href={forgotPasswordHref}
          >
            {dictionaries.signIn.forgotPasswordBtn}
          </Link>

          <Button
            className="w-full"
            disabled={hasErrors(errors) || !email || !password}
            type="submit"
          >{dictionaries.signIn.submitBtn}</Button>
        </div>
      </FormWrapper>

      <Divider text="OR" />

      <div className="flex flex-col gap-4">
        <Button
          className="flex w-full flex-row gap-2"
          variant="white"
          onClick={handleGoogle}
        >
          <Google />
          {dictionaries.signIn.continueWithGoogle}</Button>

        <Button
          className="flex w-full flex-row gap-2"
          variant="white"
          onClick={handleMicrosoft}
        >
          <Microsoft />
          {dictionaries.signIn.continueWithMicrosoft}</Button>
      </div>

    </FormCard>
  );
}
